/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import { CommunityGalleryImageVersions } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import { ComputeManagementClient } from "../computeManagementClient.js";
import {
  CommunityGalleryImageVersion,
  CommunityGalleryImageVersionsListNextOptionalParams,
  CommunityGalleryImageVersionsListOptionalParams,
  CommunityGalleryImageVersionsListResponse,
  CommunityGalleryImageVersionsGetOptionalParams,
  CommunityGalleryImageVersionsGetResponse,
  CommunityGalleryImageVersionsListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing CommunityGalleryImageVersions operations. */
export class CommunityGalleryImageVersionsImpl
  implements CommunityGalleryImageVersions
{
  private readonly client: ComputeManagementClient;

  /**
   * Initialize a new instance of the class CommunityGalleryImageVersions class.
   * @param client Reference to the service client
   */
  constructor(client: ComputeManagementClient) {
    this.client = client;
  }

  /**
   * List community gallery image versions inside an image.
   * @param location The name of Azure region.
   * @param publicGalleryName The public name of the community gallery.
   * @param galleryImageName The name of the community gallery image definition.
   * @param options The options parameters.
   */
  public list(
    location: string,
    publicGalleryName: string,
    galleryImageName: string,
    options?: CommunityGalleryImageVersionsListOptionalParams,
  ): PagedAsyncIterableIterator<CommunityGalleryImageVersion> {
    const iter = this.listPagingAll(
      location,
      publicGalleryName,
      galleryImageName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(
          location,
          publicGalleryName,
          galleryImageName,
          options,
          settings,
        );
      },
    };
  }

  private async *listPagingPage(
    location: string,
    publicGalleryName: string,
    galleryImageName: string,
    options?: CommunityGalleryImageVersionsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<CommunityGalleryImageVersion[]> {
    let result: CommunityGalleryImageVersionsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(
        location,
        publicGalleryName,
        galleryImageName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(
        location,
        publicGalleryName,
        galleryImageName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    location: string,
    publicGalleryName: string,
    galleryImageName: string,
    options?: CommunityGalleryImageVersionsListOptionalParams,
  ): AsyncIterableIterator<CommunityGalleryImageVersion> {
    for await (const page of this.listPagingPage(
      location,
      publicGalleryName,
      galleryImageName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * List community gallery image versions inside an image.
   * @param location The name of Azure region.
   * @param publicGalleryName The public name of the community gallery.
   * @param galleryImageName The name of the community gallery image definition.
   * @param options The options parameters.
   */
  private _list(
    location: string,
    publicGalleryName: string,
    galleryImageName: string,
    options?: CommunityGalleryImageVersionsListOptionalParams,
  ): Promise<CommunityGalleryImageVersionsListResponse> {
    return this.client.sendOperationRequest(
      { location, publicGalleryName, galleryImageName, options },
      listOperationSpec,
    );
  }

  /**
   * Get a community gallery image version.
   * @param location The name of Azure region.
   * @param publicGalleryName The public name of the community gallery.
   * @param galleryImageName The name of the community gallery image definition.
   * @param galleryImageVersionName The name of the community gallery image version. Needs to follow
   *                                semantic version name pattern: The allowed characters are digit and period. Digits must be within
   *                                the range of a 32-bit integer. Format: <MajorVersion>.<MinorVersion>.<Patch>
   * @param options The options parameters.
   */
  get(
    location: string,
    publicGalleryName: string,
    galleryImageName: string,
    galleryImageVersionName: string,
    options?: CommunityGalleryImageVersionsGetOptionalParams,
  ): Promise<CommunityGalleryImageVersionsGetResponse> {
    return this.client.sendOperationRequest(
      {
        location,
        publicGalleryName,
        galleryImageName,
        galleryImageVersionName,
        options,
      },
      getOperationSpec,
    );
  }

  /**
   * ListNext
   * @param location The name of Azure region.
   * @param publicGalleryName The public name of the community gallery.
   * @param galleryImageName The name of the community gallery image definition.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    location: string,
    publicGalleryName: string,
    galleryImageName: string,
    nextLink: string,
    options?: CommunityGalleryImageVersionsListNextOptionalParams,
  ): Promise<CommunityGalleryImageVersionsListNextResponse> {
    return this.client.sendOperationRequest(
      { location, publicGalleryName, galleryImageName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Compute/locations/{location}/communityGalleries/{publicGalleryName}/images/{galleryImageName}/versions",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CommunityGalleryImageVersionList,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.location,
    Parameters.publicGalleryName,
    Parameters.galleryImageName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/providers/Microsoft.Compute/locations/{location}/communityGalleries/{publicGalleryName}/images/{galleryImageName}/versions/{galleryImageVersionName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CommunityGalleryImageVersion,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion3],
  urlParameters: [
    Parameters.$host,
    Parameters.subscriptionId,
    Parameters.location,
    Parameters.publicGalleryName,
    Parameters.galleryImageName,
    Parameters.galleryImageVersionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CommunityGalleryImageVersionList,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.nextLink,
    Parameters.subscriptionId,
    Parameters.location,
    Parameters.publicGalleryName,
    Parameters.galleryImageName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
